import { sessionStore, updateSession, type SocialGiftingSettings } from "../../stores/session";
import type { FoojiData } from "../../../types/campaign";
import { DefaultAuthFlow } from "./DefaultAuthFlow";
import { AnonymousAuthFlow } from "./AnonymousAuthFlow";
import { getSearchParam } from "../document";
import { saveUserSettings } from "../../apiLoader/user";
import * as campaignApi from "../../api/campaign";
import { startPolling } from "../steps/wait";
import { createAnonymousStart } from "../steps/wait/handlers";
import * as AuthApi from "../../apiLoader/auth";
import { navigateToPath } from "../../routing";
import PATHS from "../../constants/routePaths";
import { HttpError } from "../../public/lib/api/utils";
import { handleTempJWTIfPresent } from "../jwtExchanger";

export type AuthData = {
  return_to?: string;
  fooji_data: FoojiData | object;
  client: string;
  fingerprint: string;
  fingerprint_description: string;
  language: string;
  turnstile_token?: string;
};

export const getAuthFlow = (channel: string) => {
  switch (channel) {
    case "anonymous":
      return new AnonymousAuthFlow();
    default:
      return new DefaultAuthFlow();
  }
};

const FOUR_WEEKS_IN_MILLISECONDS = 24 * 60 * 60000 * 7 * 4;

export const isExpired = (options?: { requiresExpiry?: boolean; isBase64?: boolean }) => {
  const requiresExpiry = options?.requiresExpiry ?? false;
  const isBase64 = options?.isBase64 ?? true;
  const param = getSearchParam("e");

  if (param) {
    const expiringAt = safeDecodeParam(param, isBase64);
    const now = Date.now();

    // We don't allow setting expiringAt to more than 4 weeks in the future, so we just reject it
    return now > expiringAt || expiringAt > now + FOUR_WEEKS_IN_MILLISECONDS;
  }

  return requiresExpiry;
};

const safeDecodeParam = (value: string, isBase64: boolean) => {
  try {
    return parseInt(isBase64 ? atob(value) : value) || 0;
  } catch (e) {
    return 0;
  }
};

export const handleSocialGifting = async (authData: campaignApi.AnonymousAuthData) => {
  const isFullySpecifiedSocialGifting = authData.item_key && authData.variation;

  const checksumIgnore = [];
  if (!authData.item_key) checksumIgnore.push("item_key");
  if (!authData.variation) checksumIgnore.push("variation");

  const socialGiftingSettings: SocialGiftingSettings = {
    ...authData,
    checksum_ignore: checksumIgnore
  };
  delete socialGiftingSettings.channel;
  if (!authData.skip_stock_management) {
    delete socialGiftingSettings.skip_stock_management;
  }

  // For social gift links we need to save gift to user settings
  // so the workflow knows which item to choose and so it can filter the menu and
  await saveUserSettings({
    social_gift: socialGiftingSettings
  });

  const socialGiftingMode = authData.type === "bundle" ? "bundle" : isFullySpecifiedSocialGifting ? "full" : "select";

  updateSession({
    socialGiftingMode,
    socialGifting: socialGiftingSettings
  });
};

export const waitForAnonymousStart = () => new Promise<void>((resolve) => startPolling(createAnonymousStart(resolve)));

const DEFAULT_RETRY_BACKOFF = 30000;
export const assessTurnstile = async (token: string, campaign: string) => {
  try {
    const {
      token: internalToken,
      valid_until: validUntil,
      retry_at: retryAt
    } = await AuthApi.assessTurnstile(token, campaign);

    const turnstileTokenData = { token: internalToken, validUntil, retryAt };

    sessionStore.update((data) => ({ ...data, turnstileTokenData }));
  } catch (error: unknown) {
    if (error instanceof HttpError && [403, 429].includes(error.response.status)) {
      const retryAt = error.response.status === 429
        // 429 (assess-turnstile code) will have a retry_at in the response,
        ? ((await error.response.json()) as { retry_at: string }).retry_at
        // 403 (rate-limiter code) will not
        : new Date(Date.now() + DEFAULT_RETRY_BACKOFF).toISOString();
      sessionStore.update((data) => ({
        ...data,
        turnstileTokenData: { retryAt }
      }));
    }

    throw error;
  }
};

export const completeTurnstile = async (token: string, campaign: string) => {
  try {
    await assessTurnstile(token, campaign);
  } catch (error) {
    return navigateToPath(
      (error instanceof HttpError && [403, 429].includes(error.response.status))
        ? PATHS.RATE_LIMIT_WITH_COUNTDOWN
        : PATHS.AUTHENTICATION_ERROR
    );
  }

  try {
    await handleTempJWTIfPresent();
  } catch (error) {
    navigateToPath(PATHS.AUTHENTICATION_ERROR);
    console.error("Error handling temp JWT", error);
  }

  // hasCompletedCaptcha is used to determine if the captcha process has completed
  // not if our end of the captcha process was successful...
  sessionStore.update((data) => ({
    ...data,
    turnstileTokenData: {
      ...data.turnstileTokenData,
      hasCompletedCaptchaProcess: true
    }
  }));
};
